'use client';

import { AnimatePresence, m } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TrojanLogo from './TrojanLogo';
import BrandWordmark from './BrandWordmark';

interface NavigationDropdownItem {
    name: string;
    href: string;
    section?: string;
    category?: string;
}

interface NavigationItem {
    name: string;
    href: string;
    dropdownItems?: NavigationDropdownItem[];
}

const navigationItems: NavigationItem[] = [
    { name: 'Home', href: '#hero' },
    {
        name: 'Media',
        href: '#media',
        dropdownItems: [
            { name: 'YouTube', href: '#media', section: 'youtube' },
            { name: 'Instagram', href: '#media', section: 'instagram' },
            { name: 'Live Status', href: '#media', section: 'live-status' },
        ],
    },
    {
        name: 'Gear',
        href: '#gear',
        dropdownItems: [
            { name: 'All Gear', href: '#gear', category: 'all' },
            { name: 'MIDI Controllers', href: '#gear', category: 'MIDI Controllers' },
            { name: 'Audio Interfaces', href: '#gear', category: 'Audio Interfaces' },
            { name: 'Headphones & Speakers', href: '#gear', category: 'Headphones & Speakers' },
            { name: 'Recording', href: '#gear', category: 'Recording' },
            { name: 'Software', href: '#gear', category: 'Software' },
            { name: 'Computers', href: '#gear', category: 'Computers' },
            { name: 'Misc', href: '#gear', category: 'Misc' },
        ],
    },
    {
        name: 'About',
        href: '#about',
        dropdownItems: [
            { name: 'About overview', href: '#about' },
            { name: 'Artist story', href: '/about/' },
            { name: 'Performance system', href: '/system/' },
            { name: 'Cameras & visuals', href: '/system/loopy-pro/' },
            { name: 'Software routing', href: '/system/ableton-control/' },
        ],
    },
];

interface NavigationProps {
    introActive?: boolean;
}

export default function Navigation({ introActive = false }: NavigationProps) {
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState('hero');
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const navRef = useRef<HTMLElement>(null);
    const desktopShellRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handlePointerDown = (event: PointerEvent) => {
            if (!navRef.current?.contains(event.target as Node)) {
                setActiveDropdown(null);
                setMobileOpen(false);
            }
        };
        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, []);

    useEffect(() => {
        let frame: number | null = null;
        const updateActiveSection = () => {
            frame = null;
            const sections = ['hero', 'media', 'gear', 'about'];
            const current = sections.find((section) => {
                if (section === 'hero') return window.scrollY < 240;
                const element = document.getElementById(section);
                if (!element) return false;
                const rect = element.getBoundingClientRect();
                return rect.top <= 140 && rect.bottom >= 140;
            });
            if (current) setActiveSection(current);
        };
        const handleScroll = () => {
            if (frame === null) frame = requestAnimationFrame(updateActiveSection);
        };
        updateActiveSection();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (frame !== null) cancelAnimationFrame(frame);
        };
    }, []);

    useEffect(() => {
        const shell = desktopShellRef.current;
        if (!shell) return;

        let frame: number | null = null;
        let pointer = { x: 0, y: 0 };

        const paintGlow = () => {
            frame = null;
            const surfaces: Array<{ element: HTMLElement | null; range: number }> = [
                { element: shell, range: 140 },
                ...Array.from(document.querySelectorAll<HTMLElement>('[data-nav-dropdown-glow]'))
                    .map((element) => ({ element, range: 110 })),
            ];

            surfaces.forEach(({ element, range }) => {
                if (!element || element.offsetParent === null) {
                    element?.style.setProperty('--nav-glow-opacity', '0');
                    return;
                }

                const rect = element.getBoundingClientRect();
                const horizontalDistance = Math.max(rect.left - pointer.x, 0, pointer.x - rect.right);
                const verticalDistance = Math.max(rect.top - pointer.y, 0, pointer.y - rect.bottom);
                const distance = Math.hypot(horizontalDistance, verticalDistance);
                const proximity = Math.max(0, 1 - distance / range);

                element.style.setProperty('--nav-glow-x', `${pointer.x - rect.left}px`);
                element.style.setProperty('--nav-glow-y', `${pointer.y - rect.top}px`);
                element.style.setProperty('--nav-glow-opacity', proximity.toFixed(3));
            });
        };

        const handlePointerMove = (event: PointerEvent) => {
            if (event.pointerType === 'touch') return;
            pointer = { x: event.clientX, y: event.clientY };
            if (frame === null) frame = window.requestAnimationFrame(paintGlow);
        };

        const clearGlow = () => {
            shell.style.setProperty('--nav-glow-opacity', '0');
            document.querySelectorAll<HTMLElement>('[data-nav-dropdown-glow]').forEach((element) => {
                element.style.setProperty('--nav-glow-opacity', '0');
            });
        };

        document.addEventListener('pointermove', handlePointerMove, { passive: true });
        window.addEventListener('blur', clearGlow);
        document.documentElement.addEventListener('mouseleave', clearGlow);

        return () => {
            document.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('blur', clearGlow);
            document.documentElement.removeEventListener('mouseleave', clearGlow);
            if (frame !== null) window.cancelAnimationFrame(frame);
        };
    }, []);

    const handleNavClick = (href: string) => {
        if (!href.startsWith('#')) {
            navigate(href);
            setActiveDropdown(null);
            setMobileOpen(false);
            return;
        }

        const id = href.slice(1);
        const element = document.getElementById(id);
        if (id === 'hero') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (element) {
            const top = element.getBoundingClientRect().top + window.scrollY - 88;
            window.scrollTo({ top, behavior: 'smooth' });
        }
        setActiveDropdown(null);
        setMobileOpen(false);
    };

    const handleDropdownItemClick = (item: NavigationDropdownItem) => {
        if (item.category) {
            localStorage.setItem('selectedGearCategory', item.category);
            window.dispatchEvent(new CustomEvent('gearCategoryChanged', { detail: item.category }));
        }
        handleNavClick(item.href);
        if (item.section) {
            const sectionId = item.section;
            window.setTimeout(() => {
                document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 350);
        }
    };

    const brand = (
        <a
            href="#hero"
            onClick={(event) => {
                event.preventDefault();
                handleNavClick('#hero');
            }}
            className="group flex items-center gap-2 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70"
        >
            <span
                data-nav-logo-target
                className={`flex h-8 w-7 flex-shrink-0 items-center justify-center ${introActive ? 'opacity-0' : 'opacity-100'}`}
            >
                <TrojanLogo animate={false} draw={false} variant="nav" />
            </span>
            <span className="flex flex-col justify-center">
                <BrandWordmark animated={false} className="font-display text-[1.05rem] font-semibold leading-none tracking-[-0.025em] sm:text-[1.1rem]" />
                <span className="mt-0.5 font-mono text-[0.55rem] font-semibold leading-none tracking-[0.3em] text-cyan-300/55">
                    MUSIC
                </span>
            </span>
        </a>
    );

    return (
        <nav ref={navRef} aria-label="Primary navigation" className="pointer-events-none fixed inset-x-0 top-0 z-50">
            <m.div
                ref={desktopShellRef}
                data-nav-glow
                initial={{ y: -32, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="nav-shell pointer-events-auto mx-auto mt-3 hidden h-[4.5rem] w-[min(94vw,78rem)] items-center justify-between rounded-2xl px-5 md:flex lg:px-6"
            >
                {brand}

                <div className="absolute left-1/2 flex -translate-x-1/2 items-center gap-1">
                    {navigationItems.map((item) => {
                        const sectionId = item.href.slice(1);
                        const active = activeSection === sectionId;
                        return (
                            <div
                                key={item.name}
                                className="relative"
                                onMouseEnter={() => item.dropdownItems && setActiveDropdown(item.name)}
                                onMouseLeave={() => setActiveDropdown(null)}
                                onFocus={() => item.dropdownItems && setActiveDropdown(item.name)}
                                onBlur={(event) => {
                                    if (!event.currentTarget.contains(event.relatedTarget)) setActiveDropdown(null);
                                }}
                            >
                                <a
                                    href={item.href}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        handleNavClick(item.href);
                                    }}
                                    className={`nav-link inline-flex h-10 items-center gap-1 rounded-lg px-3 lg:px-4 ${active ? 'nav-link-active' : ''}`}
                                    aria-current={active ? 'page' : undefined}
                                    aria-haspopup={item.dropdownItems ? 'menu' : undefined}
                                    aria-expanded={item.dropdownItems ? activeDropdown === item.name : undefined}
                                >
                                    {item.name}
                                    {item.dropdownItems && (
                                        <svg className={`h-3.5 w-3.5 transition-transform ${activeDropdown === item.name ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="m6 9 6 6 6-6" />
                                        </svg>
                                    )}
                                </a>

                                <AnimatePresence>
                                    {item.dropdownItems && activeDropdown === item.name && (
                                        <m.div
                                            initial={{ opacity: 0, y: 8, scale: 0.98 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, y: 6, scale: 0.98 }}
                                            transition={{ duration: 0.16 }}
                                            className="absolute left-0 top-full w-56 pt-5"
                                        >
                                            <div
                                                data-nav-glow
                                                data-nav-dropdown-glow
                                                role="menu"
                                                className="nav-shell rounded-xl p-1.5"
                                            >
                                                {item.dropdownItems.map((dropdownItem) => (
                                                    <a
                                                        key={dropdownItem.name}
                                                        href={dropdownItem.href}
                                                        role="menuitem"
                                                        onClick={(event) => {
                                                            event.preventDefault();
                                                            handleDropdownItemClick(dropdownItem);
                                                        }}
                                                        className="nav-dropdown-item flex w-full items-center rounded-lg px-4 py-2.5 text-left text-sm font-medium focus-visible:outline-none"
                                                    >
                                                        {dropdownItem.name}
                                                    </a>
                                                ))}
                                            </div>
                                        </m.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    })}
                </div>

                <a
                    href="#media"
                    onClick={(event) => {
                        event.preventDefault();
                        handleNavClick('#media');
                    }}
                    className="nav-waveform-link group ml-auto inline-flex h-10 w-14 items-center justify-center rounded-full"
                    aria-label="Go to media"
                >
                    <span className="nav-waveform" aria-hidden>
                        <span />
                        <span />
                        <span />
                        <span />
                        <span />
                    </span>
                </a>
            </m.div>

            <m.div
                initial={{ y: -24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="nav-shell pointer-events-auto mx-3 mt-3 flex h-16 items-center justify-between rounded-2xl px-4 md:hidden"
            >
                {brand}
                <div className="flex items-center">
                    <button
                        type="button"
                        onClick={() => setMobileOpen((open) => !open)}
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-black/10 text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70 dark:border-white/10 dark:text-white"
                        aria-label="Toggle navigation menu"
                        aria-expanded={mobileOpen}
                    >
                        <span className="relative h-4 w-4">
                            <span className={`absolute left-0 top-1 h-px w-4 bg-current transition-transform ${mobileOpen ? 'translate-y-1 rotate-45' : ''}`} />
                            <span className={`absolute left-0 top-2 h-px w-4 bg-current transition-opacity ${mobileOpen ? 'opacity-0' : ''}`} />
                            <span className={`absolute left-0 top-3 h-px w-4 bg-current transition-transform ${mobileOpen ? '-translate-y-1 -rotate-45' : ''}`} />
                        </span>
                    </button>
                </div>
            </m.div>

            <AnimatePresence>
                {mobileOpen && (
                    <m.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="nav-shell pointer-events-auto mx-3 mt-2 overflow-hidden rounded-2xl p-2 md:hidden"
                    >
                        {navigationItems.map((item, index) => {
                            const expanded = activeDropdown === item.name;
                            return (
                                <div key={item.name}>
                                    <div className="flex items-center">
                                        <a
                                            href={item.href}
                                            onClick={(event) => {
                                                event.preventDefault();
                                                handleNavClick(item.href);
                                            }}
                                            className={`flex flex-1 items-center justify-between rounded-xl px-4 py-3 font-display text-sm font-semibold tracking-wide ${activeSection === item.href.slice(1) ? 'bg-cyan-500/[0.08] text-cyan-700 dark:bg-white/[0.07] dark:text-cyan-200' : 'text-gray-600 dark:text-[#bbb9cc]'}`}
                                        >
                                            <span>{item.name}</span>
                                            {!item.dropdownItems && <span className="font-mono text-[10px] text-white/30">0{index + 1}</span>}
                                        </a>
                                        {item.dropdownItems && (
                                            <button
                                                type="button"
                                                onClick={() => setActiveDropdown(expanded ? null : item.name)}
                                                className="mr-1 flex h-10 w-10 items-center justify-center rounded-xl text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70 dark:text-white/45"
                                                aria-label={`${expanded ? 'Close' : 'Open'} ${item.name} menu`}
                                                aria-expanded={expanded}
                                            >
                                                <svg className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" d="m6 9 6 6 6-6" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                    <AnimatePresence initial={false}>
                                        {item.dropdownItems && expanded && (
                                            <m.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                className="overflow-hidden px-2 pb-1"
                                            >
                                                {item.dropdownItems.map((dropdownItem) => (
                                                    <a
                                                        key={dropdownItem.name}
                                                        href={dropdownItem.href}
                                                        onClick={(event) => {
                                                            event.preventDefault();
                                                            handleDropdownItemClick(dropdownItem);
                                                        }}
                                                        className="block rounded-lg px-5 py-2 text-xs text-gray-500 hover:bg-black/[0.04] hover:text-gray-900 dark:text-white/45 dark:hover:bg-white/[0.05] dark:hover:text-white"
                                                    >
                                                        {dropdownItem.name}
                                                    </a>
                                                ))}
                                            </m.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                    </m.div>
                )}
            </AnimatePresence>
        </nav>
    );
}
